import { Button } from "@material-ui/core";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { VisitLocationAddress } from "../@types/PricingModels";
import { isAU } from "../DataConstants";
import { Div } from "./GenericView";
import { _attr } from "Util/Utilities";

function getLocationText(location?: VisitLocationAddress): string {
  if (!location) return "";

  const val = (v?: string, sep = ", ") => (!v ? "" : v + sep);
  if (isAU)
    return (
      val(location.unit_number, "/") +
      val(location.street_number, " ") +
      val(location.street_address) +
      val(location.street_name, " ") +
      val(location.suburb) +
      val(location.state) +
      val(location.post_code) +
      val(location.postcode) +
      val(location.city) +
      val(location.country, "")
    );
  return (
    val(location.block_number, " ") +
    val(location.street_address) +
    val(location.street_name, " ") +
    val(location.unit_number) +
    val(location.district) +
    val(location.state) +
    val(location.post_code) +
    val(location.postcode) +
    val(location.city, " ") +
    val(location.country, "")
  );
}
const LocationDropdown: FC<any> = (props) => {
  const { location, recipientStore, showAsPopup, onChange, hasError } = props;
  const inputRef: any = useRef(null);
  const { address_list } = recipientStore?.careRecipient?.co;
  const modifiedAddressList = address_list?.map((list: any) => {
    // for consistent fields
    return {
      name: list.name,
      block_number: list.block_number,
      street_address: list.street_name,
      unit_number: list.unit_number,
      post_code: list.postcode,
      district: list.district,
      state: list.state,
      country: list.country,
      latitude: list.lat,
      longitude: list.long,
      city: list.city,
      suburb: list.suburb,
      street_number: list.street_number,
    };
  });
  const [selectedAddress, setSelectedAddress] = useState<any>();
  const [showList, setShowList] = useState(false);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (!inputRef.current.contains(event.target)) {
      setShowList(false);
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showList && showAsPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showList, showAsPopup, handleClickOutside]);
  useEffect(() => {
    const found = modifiedAddressList.find(
      (ele: any) => ele.name === location.name
    );
    const fn = onChange;
    // if location found from the list we chose this one other wise first one
    if (found) {
      setSelectedAddress(found);
      fn && fn(found, false);
    } else if (
      !selectedAddress &&
      modifiedAddressList &&
      modifiedAddressList.length > 0
    ) {
      setSelectedAddress(modifiedAddressList[0]);
      fn && fn(modifiedAddressList[0], false);
    }
  }, [modifiedAddressList.length]);

  const _save = (isApplyAll = false) => {
    if (showAsPopup) {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    const fn = onChange;
    if (!selectedAddress) {
      setSelectedAddress(modifiedAddressList[0]);
      fn && fn(modifiedAddressList[0], isApplyAll);
    } else {
      setShowList(false);
      fn && fn(selectedAddress, isApplyAll);
    }
  };

  const getSelectedRow = (address: any) => {
    if (!selectedAddress) return false;
    // to compare new address with previous
    if (
      (selectedAddress.street_address &&
        address.street_address !== selectedAddress.street_address) ||
      (selectedAddress.city && address.city !== selectedAddress.city) ||
      (selectedAddress.latitude && address.latitude !== selectedAddress.latitude) ||
      (selectedAddress.longitude &&
        address.longitude !== selectedAddress.longitude) ||
      (selectedAddress.post_code &&
        address.post_code !== selectedAddress.post_code) ||
      (selectedAddress.state && address.state !== selectedAddress.state) ||
      (selectedAddress.street_number &&
        address.street_number !== selectedAddress.street_number) ||
      (selectedAddress.suburb && address.suburb !== selectedAddress.suburb) ||
      (selectedAddress.unit_number &&
        address.unit_number !== selectedAddress.unit_number)
    ) {
      return false;
    }
    return (
      address.street_number === selectedAddress?.street_number &&
      address.unit_number === selectedAddress?.unit_number
    );
  };
  const renderList = (address: any) => {
    const isSelected = getSelectedRow(address);
    return (
      <Div
        key={address.name}
        className="ld-location-row flex justify-between center"
        onClick={() => setSelectedAddress(address)}
      >
        <div className="mr-6">
          <b>{address.name}</b> <span>{getLocationText(address)}</span>
        </div>
        {isSelected && <img src="/check_circle.svg" height={16} />}
      </Div>
    );
  };

  const renderDropdown = () => {
    if (!showList) return null;
    if (showAsPopup) {
      return (
        <div className="if-fixed-overlay display_block">
          <div
            className="if-popup if-popup-text ld-location-popup"
            ref={inputRef}
          >
            <Div>
              {modifiedAddressList?.map((address: any) => renderList(address))}
            </Div>
            <Div h vc className="justify-between" mt={20}>
              <Button
                tabIndex={0}
                className="ha-dialog-done li-done-all"
                onClick={() => _save(true)}
              >
                Apply to selected visits
              </Button>
              <Button
                tabIndex={0}
                color="primary"
                className="ha-btn-green li-done"
                variant="contained"
                onClick={() => _save()}
              >
                Save
              </Button>
            </Div>
          </div>
        </div>
      );
    }
    return (
      <div>
        <Div>
          {modifiedAddressList?.map((address: any) => renderList(address))}
        </Div>
        <Div h vc className="justify-end" mt={10}>
          <Div
            color="#9898A6"
            fs={13}
            mr={25}
            pointer
            onClick={() => setShowList(false)}
          >
            Cancel
          </Div>
          <Div color="#37bac4" pointer onClick={() => _save()}>
            Save
          </Div>
        </Div>
      </div>
    );
  };

  return (
    <div className="fs-14 w-full ld">
      <div
        className={
          hasError
            ? "location-error-highlight if-input if-input-field if-type-text curson-text"
            : "if-input if-input-field if-type-text curson-text"
        }
        has-focus={_attr(showList)}
        onClick={() => setShowList(true)}
      >
        <div>
          <b>{location.location_name ?? location.name}</b>{" "}
          <span>{getLocationText(location)}</span>
        </div>
      </div>
      {renderDropdown()}
    </div>
  );
};
const mapStateToProps = (state: any) => {
  const { recipientStore } = state;
  return {
    ...state,
    crList: recipientStore.crList,
    coList: recipientStore.coList,
  };
};

export default connect<any, any>(mapStateToProps, {})(LocationDropdown);
